import { TooltipDirective } from '../tooltip/tooltip.directive';
import { BOTONES } from '../../componentes/botones';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { MatCardModule } from '@angular/material/card';
import { CardHeaderComponent } from '../../componentes/card-header/card-header.component';

/** Offset del drag respecto del ancla del overlay. */
export interface PosicionDrag {
  x: number;
  y: number;
}

/** Cambio de posición emitido al soltar el drag, con el estado en que ocurrió. */
export interface CambioPosicion extends PosicionDrag {
  minimizado: boolean;
}

export interface ExcesoActividadData {
  /** Minutos de uso continuo que dispararon el banner (solo para el mensaje) */
  minutosActividad: number;
  /** Minutos que dura el descanso sugerido (countdown tras aceptar) */
  minutosDescanso: number;
  /** Reanudación tras refresco: si viene, el diálogo arranca directo en fase
   *  descanso con estos segundos restantes (sin pasar por el aviso). */
  segundosRestantesInicial?: number;
  /** Reanudación tras refresco: si estaba minimizado, arranca como chip. */
  minimizadoInicial?: boolean;
  /** Reanudación tras refresco: posición del drag en cada estado. */
  posicionMinimizadoInicial?: PosicionDrag;
  posicionExpandidoInicial?: PosicionDrag;
}

/**
 * Banner informativo (NO bloqueante) de periodo de actividad. Se abre sin
 * backdrop, así el operador puede seguir trabajando e ignorarlo. Tiene una
 * sola acción "Entendido": al aceptarla arranca el countdown del descanso
 * (fijo, `minutosDescanso`) y, al terminar, el diálogo se cierra devolviendo
 * `true` para que el servicio resetee el contador de uso.
 */
@Component({
  selector: 'app-exceso-actividad-dialog',
  templateUrl: './exceso-actividad-dialog.component.html',
  styleUrls: ['./exceso-actividad-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipDirective, 
    DecimalPipe,
    MatDialogModule,
    BOTONES,
    MatIconModule,
    MatProgressBarModule,
    
    DragDropModule,
    MatCardModule,
    CardHeaderComponent,
  ],
})
export class ExcesoActividadDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private matDialogRef =
    inject<MatDialogRef<ExcesoActividadDialogComponent, boolean>>(MatDialogRef);
  public data = inject<ExcesoActividadData>(MAT_DIALOG_DATA);

  /** 'aviso' = mensaje + botón; 'descanso' = countdown del descanso */
  public fase: 'aviso' | 'descanso' = 'aviso';
  /** En fase descanso, colapsa el diálogo a un chip con el countdown. */
  public minimizado = false;

  /** Emite cuando el operador acepta y arranca el descanso. El servicio lo usa
   *  para pausar el control de inactividad mientras dura el descanso. */
  @Output() descansoIniciado = new EventEmitter<void>();

  /** Emite el estado minimizado/expandido para que el servicio lo persista y
   *  lo reanude tras un refresco. */
  @Output() minimizadoChange = new EventEmitter<boolean>();

  /** Emite la posición del drag (con el estado) al soltar, para persistirla. */
  @Output() posicionChange = new EventEmitter<CambioPosicion>();

  /** Drag del panel (root = pane del overlay). */
  @ViewChild(CdkDrag) private drag?: CdkDrag;

  /** Posición del drag recordada por estado. El drag es un solo elemento, pero
   *  cada estado (chip vs diálogo) tiene tamaño distinto, así que guardamos una
   *  posición para cada uno. */
  private posMinimizado?: PosicionDrag;
  private posExpandido?: PosicionDrag;

  /** Se prende cuando el chip se arrastró: evita que el mouseup del drag dispare
   *  el click y expanda. Sólo se expande con un click real (sin arrastre). */
  private huboDrag = false;
  private posicionTimeout?: NodeJS.Timeout;

  private readonly totalDescanso = Math.max(
    1,
    Math.round(this.data.minutosDescanso * 60),
  );
  public segundosRestantes = this.totalDescanso;
  private interval?: NodeJS.Timeout;

  public get progreso(): number {
    return (this.segundosRestantes / this.totalDescanso) * 100;
  }

  /** MM:SS del descanso restante */
  public get tiempoRestante(): string {
    const m = Math.floor(this.segundosRestantes / 60);
    const s = this.segundosRestantes % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  ngOnInit(): void {
    this.posMinimizado = this.data.posicionMinimizadoInicial;
    this.posExpandido = this.data.posicionExpandidoInicial;
    // Reanudación tras refresco: el servicio abre el diálogo directo en descanso
    // con los segundos que restaban. No emite descansoIniciado (el servicio ya
    // sabe que sigue el descanso; emitirlo recalcularía el fin y lo estiraría).
    if (this.data.segundosRestantesInicial != null) {
      this.fase = 'descanso';
      this.segundosRestantes = Math.max(1, this.data.segundosRestantesInicial);
      this.iniciarCountdown();
      if (this.data.minimizadoInicial) {
        this.minimizar();
      }
    }
  }

  ngAfterViewInit(): void {
    // Diferido: en este punto el overlay usa GlobalPositionStrategy (bottom/
    // right) que se aplica en un macrotask posterior; si seteamos la posición
    // del drag ya, el offset parte de una base equivocada y no pega visualmente.
    // Un tick después el pane ya está anclado y setFreeDragPosition ubica bien.
    this.posicionTimeout = setTimeout(() => this.aplicarPosicion());
  }

  /** El operador acepta: arranca el descanso fijo. */
  public aceptar(): void {
    if (this.fase === 'descanso') return;
    this.fase = 'descanso';
    this.descansoIniciado.emit();
    this.iniciarCountdown();
  }

  private iniciarCountdown(): void {
    this.interval = setInterval(() => {
      this.segundosRestantes--;
      if (this.segundosRestantes <= 0) {
        clearInterval(this.interval!);
        // Descanso completo → el servicio resetea el contador de uso.
        this.matDialogRef.close(true);
      }
    }, 1_000);
  }

  /** Colapsa a chip. El countdown sigue corriendo: sólo cambia tamaño/estilo. */
  public minimizar(): void {
    this.minimizado = true;
    this.matDialogRef.addPanelClass('exceso-actividad-min');
    this.matDialogRef.updateSize('auto', 'auto');
    this.aplicarPosicion();
    this.minimizadoChange.emit(true);
  }

  /** Restaura el diálogo completo. */
  public expandir(): void {
    this.minimizado = false;
    this.matDialogRef.removePanelClass('exceso-actividad-min');
    this.matDialogRef.updateSize('420px', 'auto');
    this.aplicarPosicion();
    this.minimizadoChange.emit(false);
  }

  /** Aplica la posición recordada del estado actual; si no hay, vuelve al ancla
   *  (abajo-derecha) → nunca queda fuera de pantalla. */
  private aplicarPosicion(): void {
    if (!this.drag) return;
    const pos = this.minimizado ? this.posMinimizado : this.posExpandido;
    if (pos) {
      this.drag.setFreeDragPosition(pos);
    } else {
      this.drag.reset();
    }
  }

  /** Marca que hubo arrastre para no expandir en el mouseup del drag. */
  public onDragStarted(): void {
    this.huboDrag = true;
  }

  /** Al soltar el drag, guarda la posición para el estado actual y la emite. */
  public onDragEnded(): void {
    if (!this.drag) return;
    const pos = this.drag.getFreeDragPosition();
    if (this.minimizado) {
      this.posMinimizado = pos;
    } else {
      this.posExpandido = pos;
    }
    this.posicionChange.emit({
      minimizado: this.minimizado,
      x: pos.x,
      y: pos.y,
    });
  }

  /** Click en el chip minimizado: expande sólo si NO fue un arrastre. */
  public onMiniClick(): void {
    if (this.huboDrag) {
      this.huboDrag = false;
      return;
    }
    this.expandir();
  }

  ngOnDestroy(): void {
    clearInterval(this.interval!);
    clearTimeout(this.posicionTimeout!);
  }
}
